import { useMemo, useState } from "react";
import {
  Box,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import type { RagImageItem } from "../../types/ragChatTypes";
import {
  buildOrderedImageQueue,
  cleanStepDisplayText,
  computeProportionalStepImages,
  parseNumberedSteps,
  parseSegments,
  type StepImageBinding,
} from "../../utils/ragChatUtils";
import { RagImageLightbox } from "./RagImageLightbox";
import { RagMarkdownContent } from "./RagMarkdownContent";
import { useTranslation } from "react-i18next";

function InlineFigure({
  item,
  page,
  onOpen,
  zoomLabel,
  pageLabel,
  indented = false,
}: {
  item: RagImageItem;
  page: string;
  onOpen: (src: string) => void;
  zoomLabel: string;
  pageLabel: string;
  indented?: boolean;
}) {
  const label = (item.path || "diagram").split("/").pop() || "diagram";
  const src = item.path;
  if (!src) return null;
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(src)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(src);
        }
      }}
      aria-label={`${zoomLabel} ${pageLabel}${page}`}
      title={label}
      sx={{
        my: 1.5,
        ml: indented ? { xs: 0, sm: 5.5 } : 0,
        width: "100%",
        maxWidth: 480,
        textAlign: "left",
        p: 0,
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        cursor: "zoom-in",
        boxShadow: "0 1px 4px rgba(28,46,54,0.08)",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(28,46,54,0.14)",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          aspectRatio: "4 / 3",
          bgcolor: "#eef3f8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={src}
          alt={label}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>
      <Typography
        variant="labelSmall"
        sx={{
          px: 1.25,
          py: 0.6,
          bgcolor: "grey.50",
          color: "text.secondary",
          fontWeight: 600,
        }}
      >
        {pageLabel}
        {page}
      </Typography>
    </Box>
  );
}

interface RagAnswerTextProps {
  text: string;
  inlineImages: Record<string, RagImageItem[]>;
  showRichMedia?: boolean;
}

export function RagAnswerText({
  text,
  inlineImages,
  showRichMedia = true,
}: RagAnswerTextProps) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<string | null>(null);
  const steps = parseNumberedSteps(text);
  const hasSteps = steps && steps.some((s) => s.num);

  const stepImagesByIndex = useMemo(() => {
    if (!hasSteps || !showRichMedia || !steps) return {} as Record<number, StepImageBinding[]>;
    const orderedQueue = buildOrderedImageQueue(inlineImages);
    const proportional = computeProportionalStepImages(steps, orderedQueue);
    let imgQueueIdx = 0;
    const perStep: Record<number, StepImageBinding[]> = {};

    steps.forEach((step, i) => {
      if (!step.num) return;
      const citedPages = Array.from(step.text.matchAll(/\[p\.(\d+)\]/g), (m) => m[1]);
      if (citedPages.length > 0) {
        perStep[i] = citedPages.flatMap((p) =>
          (inlineImages[p] ?? []).map((img) => ({ img, page: p }))
        );
      } else if (/\[img:\d+\]/.test(step.text) && imgQueueIdx < orderedQueue.length) {
        perStep[i] = [orderedQueue[imgQueueIdx++]];
      } else {
        perStep[i] = proportional[i] ?? [];
      }
    });
    return perStep;
  }, [hasSteps, showRichMedia, steps, inlineImages]);

  if (!hasSteps) {
    const segments = parseSegments(text);
    const prose = text.replace(/\[p\.\d+\]/g, "").replace(/\[img:\d+\]\s*/g, "");
    return (
      <Box>
        <RagMarkdownContent text={prose} />
        {showRichMedia &&
          segments
            .filter((s) => s.page && inlineImages[s.page]?.length)
            .map((seg, i) =>
              (inlineImages[seg.page!] ?? []).map((img, j) => (
                <InlineFigure
                  key={`${i}-${j}`}
                  item={img}
                  page={seg.page!}
                  onOpen={setLightbox}
                  pageLabel={t("opva.chatAssistancePageShort")}
                  zoomLabel={t("opva.chatAssistanceClickToEnlarge")}
                />
              ))
            )}
        <RagImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
      </Box>
    );
  }

  return (
    <>
      <List disablePadding sx={{ mt: 0 }}>
        {steps!.map((step, i) => {
          if (!step.num) {
            if (!step.text.trim()) return null;
            return (
              <ListItem key={i} sx={{ display: "block", py: 0.5, pl: 0 }}>
                <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  <RagMarkdownContent text={cleanStepDisplayText(step.text)} dense />
                </Box>
              </ListItem>
            );
          }

          const clean = cleanStepDisplayText(step.text);
          const stepImgs = showRichMedia ? stepImagesByIndex[i] ?? [] : [];

          return (
            <ListItem key={i} alignItems="flex-start" sx={{ py: 1, pl: 0 }}>
              <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: 13,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  {step.num}
                </Box>
                <Box flex={1} minWidth={0}>
                  <RagMarkdownContent text={clean} dense />
                  {stepImgs.map(({ img, page }, j) => (
                    <InlineFigure
                      key={`${page}-${j}`}
                      item={img}
                      page={page}
                      onOpen={setLightbox}
                      pageLabel={t("opva.chatAssistancePageShort")}
                      zoomLabel={t("opva.chatAssistanceClickToEnlarge")}
                      indented
                    />
                  ))}
                </Box>
              </Box>
            </ListItem>
          );
        })}
      </List>
      <RagImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
